import React, { useMemo, useState } from 'react'
import { theme } from './warehouseTheme'
import { displayCondition } from './conditionDisplay'
import ProductTypeSelector, { masterProductTypes } from './ProductTypeSelector'
import { toPounds } from '../pricing/repriceMath.js'
import { PART_CONDITIONS, MAX_SPLIT_PARTS, MIN_SPLIT_PARTS, splitFooter } from './splitItem'

/**
 * The "Sell as" card on Testing (Trac #1111): One listing, or Split into
 * separate listings with a table of parts. Pure screen: the split state and
 * every rule live in splitItem.js and Testing.jsx owns the state.
 */

const inputStyle = {
  width: '100%', padding: '10px 12px', border: `1px solid ${theme.colors.grey300}`,
  borderRadius: theme.radius.md, fontSize: '14px', background: theme.colors.grey100, outline: 'none',
  fontFamily: 'inherit'
}
const headStyle = { textAlign: 'left', fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: theme.colors.grey500, padding: '8px 6px' }
const cellStyle = { padding: '6px', borderTop: `1px solid ${theme.colors.grey200}`, verticalAlign: 'middle' }

function ModeToggle({ split, onOneListing, onSplit }) {
  const segment = (active) => ({
    padding: '10px 18px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '14px',
    background: active ? theme.colors.charcoal : theme.colors.white,
    color: active ? theme.colors.white : theme.colors.charcoal
  })
  return (
    <div role="group" aria-label="Sell as" style={{ display: 'inline-flex', border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md, overflow: 'hidden' }}>
      <button type="button" aria-pressed={!split} style={segment(!split)} onClick={onOneListing}>One listing</button>
      <button type="button" aria-pressed={!!split} style={segment(!!split)} onClick={onSplit}>Split into separate listings</button>
    </div>
  )
}

function PartRow({ part, index, canRemove, onUpdatePart, onRemovePart, onPickCategory }) {
  const label = `Part ${index + 1}`
  return (
    <tr>
      <td style={cellStyle}>
        <input aria-label={`${label} name`} style={inputStyle} value={part.name} placeholder="Name of this part"
          onChange={e => onUpdatePart(index, { name: e.target.value })} />
      </td>
      <td style={cellStyle}>
        <button type="button" aria-label={`${label} category`} title={part.mainCategory || undefined} onClick={() => onPickCategory(index)}
          style={{ ...inputStyle, textAlign: 'left', cursor: 'pointer', color: part.subCategory ? theme.colors.charcoal : theme.colors.grey500 }}>
          {part.subCategory || 'Choose category'}
        </button>
      </td>
      <td style={cellStyle}>
        <select aria-label={`${label} condition`} style={inputStyle} value={part.condition}
          onChange={e => onUpdatePart(index, { condition: e.target.value })}>
          <option value="" disabled>Condition</option>
          {PART_CONDITIONS.map(c => <option key={c} value={c}>{displayCondition(c)}</option>)}
        </select>
      </td>
      <td style={cellStyle}>
        <input aria-label={`${label} sell price`} style={{ ...inputStyle, width: '84px' }} inputMode="decimal" value={part.sellPrice}
          placeholder="£0.00" onChange={e => onUpdatePart(index, { sellPrice: e.target.value })} />
      </td>
      <td style={cellStyle}>
        <input aria-label={`${label} cost share`} style={{ ...inputStyle, width: '84px', fontFamily: 'ui-monospace, monospace' }} inputMode="decimal"
          value={part.costShare} onChange={e => onUpdatePart(index, { costShare: e.target.value })} />
      </td>
      <td style={{ ...cellStyle, width: '28px', textAlign: 'center' }}>
        <button type="button" aria-label={`Remove ${label}`} disabled={!canRemove} onClick={() => onRemovePart(index)}
          style={{ border: 'none', background: 'transparent', fontSize: '18px', color: theme.colors.grey500, cursor: canRemove ? 'pointer' : 'not-allowed', opacity: canRemove ? 1 : 0.3 }}>
          ×
        </button>
      </td>
    </tr>
  )
}

function Footer({ footer }) {
  const money = (pence) => `£${toPounds(pence)}`
  const colour = footer.balanced ? theme.colors.greenOnions : theme.colors.crimson
  return (
    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '13px', textAlign: 'right' }}>
      Sell {money(footer.sellPence)} ·{' '}
      <strong data-testid="split-footer-cost" style={{ color: colour }}>
        {footer.balanced
          ? `Cost ${money(footer.costPence)} = buy price ✓`
          : `Cost ${money(footer.costPence)}, ${money(Math.abs(footer.differencePence))} ${footer.differencePence > 0 ? 'over' : 'under'} the buy price`}
      </strong>
    </div>
  )
}

export function SellAsPanel({ split, lineBuy, onOneListing, onSplit, onAddPart, onRemovePart, onUpdatePart }) {
  const [pickingIndex, setPickingIndex] = useState(null)
  const groups = useMemo(() => masterProductTypes(), [])
  const footer = split ? splitFooter(split, lineBuy) : null

  const pickCategory = (sub, main) => {
    onUpdatePart(pickingIndex, { subCategory: sub, mainCategory: main })
    setPickingIndex(null)
  }

  return (
    <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, border: `1px solid ${theme.colors.grey200}`, boxShadow: theme.shadows.sm, padding: '20px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700 }}>Sell as</div>
          <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Split when the parts will sell better on their own.</div>
        </div>
        <ModeToggle split={split} onOneListing={onOneListing} onSplit={onSplit} />
      </div>

      {split && (
        <>
          {pickingIndex !== null && (
            <div style={{ marginTop: '16px' }}>
              <ProductTypeSelector
                productTypes={groups}
                currentSelection={split.parts[pickingIndex].subCategory}
                onSelect={pickCategory}
                onCancel={() => setPickingIndex(null)}
              />
            </div>
          )}
          <div style={{ overflowX: 'auto', marginTop: '12px' }}>
            <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...headStyle, width: '30%' }}>Part</th><th style={headStyle}>Category</th><th style={headStyle}>Condition</th>
                  <th style={headStyle}>Sell price</th><th style={headStyle}>Cost share</th><th style={headStyle} />
                </tr>
              </thead>
              <tbody>
                {split.parts.map((part, i) => (
                  <PartRow key={i} part={part} index={i} canRemove={split.parts.length > MIN_SPLIT_PARTS}
                    onUpdatePart={onUpdatePart} onRemovePart={onRemovePart} onPickCategory={setPickingIndex} />
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', margin: '12px 0', flexWrap: 'wrap' }}>
            <button type="button" onClick={onAddPart} disabled={split.parts.length >= MAX_SPLIT_PARTS}
              style={{ padding: '10px 16px', border: `1px solid ${theme.colors.grey300}`, borderRadius: theme.radius.md, background: theme.colors.white, fontWeight: 600, cursor: 'pointer' }}>
              + Add part
            </button>
            <Footer footer={footer} />
          </div>
          <div style={{ background: theme.colors.greenLight, border: `1px solid ${theme.colors.greenOnions}`, borderRadius: theme.radius.md, padding: '12px 14px', fontSize: '14px' }}>
            ✓ Each part gets its own photos, shelf and listing. The deal keeps a link to all {split.parts.length}. The customer's payout doesn't change.
          </div>
        </>
      )}
    </div>
  )
}

/** One tab per part: the form below it edits the active part's own test. */
export function PartTabs({ parts, activeIndex, onSelect }) {
  return (
    <div style={{ background: theme.colors.white, borderRadius: theme.radius.lg, border: `1px solid ${theme.colors.grey200}`, boxShadow: theme.shadows.sm, padding: '16px 20px', marginBottom: '20px' }}>
      <div style={{ fontSize: '14px', color: theme.colors.grey500, marginBottom: '10px' }}>
        Physical condition, checklist and notes apply to each part. Pick a part:
      </div>
      <div role="tablist" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {parts.map((part, i) => {
          const active = i === activeIndex
          return (
            <button key={i} type="button" role="tab" aria-selected={active} onClick={() => onSelect(i)}
              style={{
                padding: '8px 14px', borderRadius: '20px', cursor: 'pointer', fontWeight: 600, fontSize: '13px',
                border: `2px solid ${active ? theme.colors.sunburst : theme.colors.grey200}`,
                background: active ? theme.colors.amberLight : theme.colors.grey100, color: theme.colors.charcoal
              }}>
              {part.name.trim() || `Part ${i + 1}`}
            </button>
          )
        })}
      </div>
    </div>
  )
}
